import React, { ReactElement } from 'react';
import {
  Component,
  Position,
  FilterCombo,
  getUnixDate,
  PuzzleButton,
  Container,
  PuzzleInput,
  PuzzleTagField,
  FieldWrap
} from "puzzle";
import SourceFiles from "../../sections/Content/SourceFiles/SourceFiles";
import { MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";

import classNames from 'classnames';
import _ from "lodash";
import TranslationMemories from "./TranslationMemories";
import PhaseDueDates from "./PhaseDueDates";
import {
  completedSubmissionStatuses,
  getFormattedDate,
  preventDisabledTagToOpen,
  setAccessibilityModeForMenu,
  targetLanguageItemColoring,
  TM_DATA,
  tmTagRenderer
} from "../../../util/Util";
import { Menu, MenuItem } from '@progress/kendo-react-layout';
import Policies from "../../../util/Policies";
import { Field } from "../../../UI/Field/Field";
import LocalizedDatePicker from "../../../util/LocalizedDatePicker/LocalizedDatePicker";
import { hasUnfinishedTargets } from "../../../util/Util";
import { InputChangeEvent } from "@progress/kendo-react-inputs";
import { WORKFLOW } from "../../Types";
import { BATCH } from "../Types";

type PROPS = {
  batch?: BATCH,
  focusTarget?: string,
  form?: any,
  hasHumanSteps: boolean,
  isClosable: boolean,
  moduleId: string,
  moveToTargets: BATCH[],
  multipleBatches: boolean,
  onChange: (moduleId: string, action: string, event?: any) => void,
  paClient: number,
  partiallyAnalyzed: boolean,
  project: number,
  projectFileFormats: any[],
  projectPolicies?: any,
  sourceLanguage: string,
  submissionDueDate: number,
  submissionId?: string,
  submissionMode: string,
  submissionStarted: boolean,
  submissionStatus?: string,
  targetLanguageData?: any[],
  uploadFlowState?: any,
  values?: any,
  workflows: WORKFLOW[],
  zIndexCls?: string
};
type STATE = {};

class AbstractSection extends Component<PROPS, STATE> {
  pddButton: any = React.createRef();
  tmsButton: any = React.createRef();
  state = {
    name: null,
    extra: false,
    extraType: 'tms',
    workflowData: { phases: [] }
  };
  menuRef: any
  tooltip: any;

  static defaultProps: PROPS = {
    hasHumanSteps: true,
    isClosable: true,
    moduleId: '',
    moveToTargets: [],
    multipleBatches: false,
    onChange: () => {
    },
    paClient: 0,
    partiallyAnalyzed: false,
    project: 0,
    projectFileFormats: [],
    sourceLanguage: '',
    submissionDueDate: 0,
    submissionMode: '',
    submissionStarted: false,
    workflows: []
  }

  /**
   * Used for delaying form(withFormControl) change
   */
  delayedOnChange = _.debounce((event: InputChangeEvent) => {
    const { onChange, moduleId } = this.props;
    onChange(moduleId, '', event);
  }, 500);

  /**
   * @inheritDoc
   */
  componentDidMount(): void {
    super.componentDidMount();
    const { values, project, moduleId } = this.props;
    if (values) {
      this.setFormData(values);

      const { workflowId } = this.props.values;
      if (workflowId) {
        this.getWorkflowDetails(project, workflowId, moduleId);
      }
    }
    setAccessibilityModeForMenu(this.menuRef?.menuWrapperEl, this.translate('detailsLabel'));
  };

  /**
   * @inheritDoc
   */
  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any): void {
    const { values, project, moduleId } = this.props;
    const propsChanged = !_.isEqual(prevProps, this.props);
    if (propsChanged && values) {
      this.setFormData(values, true);

      const { workflowId } = this.props.values;
      const oldWorkflowId = prevProps.values?.workflowId;
      if (workflowId && workflowId !== oldWorkflowId) {
        this.getWorkflowDetails(project, workflowId, moduleId);
      }
    }
  };

  /**
   * Check if workflow exist
   * When workflow is edited, id is changed and saved workflow cannot be accessed anymore.
   * @param workflowId
   */
  checkIfWorkflowExist = (workflowId: number) => {
    const { workflows } = this.props;
    let workflowExist = _.find(workflows, { id: workflowId });

    return workflowExist;
  }


  /**
   * Checks if all dueDates/phases are completed
   */
  isBatchCompleted = () => {
    let batchIsCompleted = true;
    let current;
    const targetLanguageInfos = this.props.values?.targetLanguageInfos;
    let item = targetLanguageInfos.length;
    let item1;
    while (batchIsCompleted && item--) {
      current = targetLanguageInfos[item];
      item1 = current.dueDateInfos?.length;
      if (item1) {
        while (batchIsCompleted && item1--) {
          batchIsCompleted = current.dueDateInfos[item1].completed;
        }
      } else {
        batchIsCompleted = false;
      }
    }
    return batchIsCompleted;
  };

  /**
   * DueDate field render
   */
  addDueDateField = () => {
    const {
      form,
      zIndexCls,
      submissionStatus = '',
      submissionMode,
      projectPolicies,
      hasHumanSteps,
      batch
    } = this.props;

    const {
      dueDate,
    } = form.getFields();

    let datePickerDisabled: boolean = false;
    if (submissionMode === "EDIT") {
      datePickerDisabled = completedSubmissionStatuses.includes(submissionStatus);
      if (!datePickerDisabled) {
        datePickerDisabled = !projectPolicies.hasEditDueDatePolicy || (!!batch && !hasUnfinishedTargets(batch.targets));
      } else {
        datePickerDisabled = !(projectPolicies.hasEditDueDatePolicy && projectPolicies.hasForcedEditDueDatePolicy)
      }

      if (!hasHumanSteps) {
        datePickerDisabled = false;
      }
    }
    return (
      <Field
        mandatory
        className={'tp-date-field'
        }
      >
        <LocalizedDatePicker
          format={this.getDateFormat(dueDate.value)}
          formatPlaceholder={getFormattedDate(this.translate)}
          disabled={!!datePickerDisabled}
          label={this.translate('dueDate')}
          min={new Date()}
          name='dueDate'
          id={`${this.props.moduleId}-dueDate`}
          required={true}
          onChange={this.onDateChangeHandler}
          popupSettings={{ popupClass: zIndexCls }}
          valid={dueDate?.valid !== false}
          errorTxt={this.translateErrorMessage(dueDate)}
          withValidation
          {...dueDate}
        />
      </Field>
    );
  };

  /**
   * Determine should dueDate be set for formControl or not
   * @param values
   */
  addDueDate = (values: any) => {
    return values?.dueDate || values?.dueDate === null ? { dueDate: values.dueDate } : null
  };

  /**
   * Menu dropdown renderer caring actions for either copy or clone batch details
   */
  batchDetailsActionsRenderer() {
    const { clearBatchDetailsClass, copyBatchDetailsClass } = this.getSectionButtonsStyles();

    return (
      <div
        className={`k-menu-primary ${this.props.submissionStarted ? 'k-disabled' : ''} tp-batch-details-menu k-hbox k-align-items-center`}>
        {/*Not possible to have openOnClick={true} and possibility to open menu on enter*/}
        <Menu hoverOpenDelay={300} hoverCloseDelay={200} vertical={true} onSelect={(e) => {
          const data = e.item.data;
          if (data) {
            if (data.operation === 'clear') {
              this.props.onChange(this.props.moduleId, 'clear');
            } else {
              this.props.onChange(this.props.moduleId, 'copy');
            }
          }
        }} ref={(element) => this.menuRef = element}>
          <MenuItem>
            <MenuItem
              text={this.translate('clearBatchDetails')}
              data={{ operation: 'clear' }}
              cssClass={clearBatchDetailsClass}
            />
            <MenuItem
              text={this.translate('copyBatchDetails')}
              data={{ operation: 'copy' }}
              cssClass={copyBatchDetailsClass}
            />
          </MenuItem>
        </Menu>
        <h3 className='tp-text-semibold tp-font-size-md'>{this.translate('detailsLabel')}</h3>
      </div>
    );
  }

  /**
   * Creates custom rule that checks
   * @param names
   */
  checkBatchNameRule = (names: any[]) => {
    return {
      rules: [
        (value: string) => ({
          valid: value.search('/') === -1,
          'data-error': 'v_batchNameIllegalCharacters'
        }),
        (value: string[]) => ({
          valid: !names.includes(value),
          'data-error': 'v_batchNameError'
        }),
        (value: string) => ({
          valid: value.length < 65,
          'data-error': 'v_batchNameTooLong'
        })
      ]
    }
  };

  closeExtra = (): void => {
    this.setState({ extra: false });
  };

  /**
   * Determine should upload within section be disabled
   */
  disableUpload = () => {
    return this.props.submissionStarted
  };

  /**
   * Checks if object contains some of given props if that's the case returns datePickerFormat else returns emptyPickerFormat as DatePickerFormat
   */
  getDateFormat = (value: any): string => {
    const dateFormat = value ? this.translateByNamespace('date.datePickerFormat') : this.translate('emptyPickerFormat');
    return dateFormat
  };

  getSectionButtonsStyles = () => {
    const {
      dueDate,
      name,
      targetLanguage,
      workflowId,
    } = this.props.form.getFields();

    const clearBatchDetailsClass = classNames('tp-clear-batch-details', {
      'k-disabled': !(name.value || workflowId.value || targetLanguage.value.length || dueDate.value)
    });
    const copyBatchDetailsClass = classNames('tp-copy-batch-details', {
      'k-disabled': !(name.value && workflowId.value && targetLanguage.value.length && dueDate.value)
    });
    return {
      clearBatchDetailsClass,
      copyBatchDetailsClass
    }
  };

  /**
   * Performs full validation on section
   */
  getValidation = () => {
    let isValidForm = this.props.form.runFullValidation();
    let isValidPhases = !this.isPhasesOverdue();
    return isValidForm && isValidPhases;
  };

  /**
   * Get workflow data from section
   */
  getWorkflowData = () => {
    const { workflowData } = this.state;

    return workflowData;
  }

  /**
   * Get workflow details data
   */
  getWorkflowDetails = (projectId: number, workflowId: number, moduleId: string) => {
    const { submissionId, submissionStarted } = this.props;
    const selectedWorkflowData = this.checkIfWorkflowExist(workflowId);
    if (selectedWorkflowData) {
      if (submissionId && submissionStarted) {
        this.dispatch('getWorkflowDetailsForStartedSubmission', Position.end, {
          projectId,
          workflowId,
          moduleId,
          submissionId
        })
      } else {
        if (selectedWorkflowData.phases?.length) {
          this.dispatch('setWorkflowDetailsRequestData', Position.end, { moduleId, workflowDetailsRequest: selectedWorkflowData });
        } else {
          this.dispatch('getWorkflowDetails', Position.end, { projectId, workflowId, moduleId });
        }
      }
    }
  }

  /**
   * Check is some phase date overdue
   */
  isPhasesOverdue = () => {
    let isOverdue = false;
    let phases = this.props.values?.targetLanguageInfos?.map((item: any) => item.dueDateInfos);
    if (phases) {
      phases = _.flatten(phases);
      let phasesLength = phases.length;
      let current;
      let newDate = getUnixDate(new Date());
      while (phasesLength && !isOverdue) {
        phasesLength--;
        current = phases[phasesLength];
        if (current && !current.completed && current.dueDate < newDate) {
          isOverdue = true;
        }
      }
    } else {
      let unixDate = getUnixDate(this.props.values?.dueDate);
      isOverdue = unixDate < getUnixDate(new Date());
      if (!unixDate) {
        isOverdue = false;
      }
    }

    return isOverdue;
  }

  /**
   * Changes batch due date, in cases where batch date exceeds submission date changes submission date too.
   * @param e
   */
  onDateChangeHandler = (e: any) => {
    const { onChange, moduleId, submissionDueDate } = this.props;
    if (e.value > submissionDueDate) {
      this.dispatch('changeSubmissionDueDate', '', {
        date: e.value,
        moduleId,
        fromDate: this.translateByNamespace('date.dateWithTime', {
          date: submissionDueDate
        }),
        toDate: this.translateByNamespace('date.dateWithTime', {
          date: e.value
        })
      })
    } else {
      onChange(moduleId, '', e)
    }

  };

  /**
   * Delayed batchName change (PDII-36870)
   */
  onInputChangeDelay = (event: InputChangeEvent) => {
    this.setState({
      name: event.value
    });

    this.delayedOnChange(event);
  };

  /**
   * Close extra popup and change target language
   */
  onTargetLanguageChange = (event: MultiSelectChangeEvent, moduleId: string): void => {
    this.dispatch('closeExtra', '', { moduleId });
    this.props.onChange(moduleId, '', event);
  };

  /**
   * Open extra popup through dispatcher for validation purposes
   */
  setExtraDispatcher = (type: string): void => {
    const { moduleId } = this.props;
    this.dispatch('openTM', '', { moduleId, type });
  };

  /**
   * Open extra popup
   */
  setExtra = (type: string): void => {
    this.setState((prevState: any) => ({
      ...prevState,
      extra: !prevState.extra,
      extraType: type
    }));
  };

  /**
   * Sets sections details.
   * @param addRules
   * @param values
   */
  setFormData = (values: any, addRules?: boolean) => {
    const { setData } = this.props.form;
    let details: any = {};
    Object.assign(details,
      values?.name || values?.name === "" ? { name: values.name } : null,
      values?.workflowId || values?.workflowId === null ? { workflowId: values.workflowId } : null,
      values?.targetLanguage ? { targetLanguage: values.targetLanguage } : null,
      this.addDueDate(values)
    );
    let batchNotEmpty = values.name && values.targetLanguage;
    if (batchNotEmpty && !this.checkIfWorkflowExist(values.workflowId)) {
      details.workflowId = null;
    }
    addRules ? setData(details, { name: this.checkBatchNameRule(values.otherBatchNames) }) : setData(details);
  };

  setSpecificDate = (date: number, moduleId: string) => {
    const { onChange } = this.props;
    let dateObj = new Date(date);
    let e = {
      target: {
        props: {
          name: 'dueDate'
        },
        value: dateObj
      }
    };
    onChange(moduleId, '', e)
  }

  /**
   * Set workflow details data
   */
  setWorkflowDetails = (workflowData: any) => {
    this.setState({ workflowData });
  }

  renderBody(): JSX.Element {
    const {
      batch,
      focusTarget,
      form,
      isClosable,
      onChange,
      moduleId,
      moveToTargets,
      multipleBatches,
      paClient,
      partiallyAnalyzed,
      project,
      projectFileFormats,
      sourceLanguage,
      submissionMode,
      submissionStarted,
      submissionStatus = '',
      targetLanguageData,
      uploadFlowState,
      workflows,
      zIndexCls
    } = this.props;

    const {
      name,
      targetLanguage,
      targetFormat,
      workflowId,
    } = form.getFields();

    const { extraType, extra, workflowData } = this.state;

    const deleteBatchBtnClass = classNames('tp-close-batch-section', {
      'k-state-disabled': submissionStarted
    });

    const workflowsClass = 'k-mandatory tp-form-field-workflow';
    const targetLanguages = classNames('k-mandatory tp-form-field-target-language');

    const isTemplate = submissionMode === 'TEMPLATE_CREATE' || submissionMode === 'TEMPLATE_EDIT';
    const templateBatchSectionClass = isTemplate ? 'tp-template-batch-section' : '';

    const phasesData = workflowData.phases;
    const hasEditPhaseDueDatePolicy = submissionMode === 'EDIT' ? Policies.inProject(project)?.hasEditPhaseDueDatePolicy : true;

    const pddBtnCls = classNames({
      'k-button-high-priority': this.isPhasesOverdue(),
      'k-selected': extraType === 'pdd' && extra
    })

    let targetLanguagesForPhases = targetLanguageData?.filter((item: any) => targetLanguage.value.includes(item.targetLanguage));
    return (
      <div className={'tp-batch-section-warp'}>
        <div
          className={`tp-batch-section ${moduleId} ${moduleId === focusTarget ? 'qa-fakeClass-focused-batch' : ''} ${templateBatchSectionClass}`}>
          <Container
            ariaLabel={this.translate('SRL-section-details')}
            className={'tp-batch-creation-form'}
            htmlTag={'section'}
            tabIndex={0}
          >
            {this.batchDetailsActionsRenderer()}
            <FieldWrap
              layout={[6, 6]}
              withValidation
            >
              <PuzzleInput
                asField
                fieldMandatory
                className={'tp-form-field-batch-name'}
                label={this.translate('batchName')}
                id={`${moduleId}-batchName`}
                name={"name"}
                onChange={this.onInputChangeDelay}
                errorTxt={this.translateErrorMessage(name)}
                withValidation
                {...name}
                value={this.state.name ?? name.value} // ?? - nullish operator. If this.state.name is null or undefined it will use the right side, otherwise it will use the left.
              />
              <Field
                mandatory
                withAdditionalEl
              >
                {this.addDueDateField()}
                {!isTemplate &&
                  <PuzzleButton
                    titleText={this.translate("SRL-openPhaseDueDatePopup")}
                    ref={(el: any) => this.pddButton = el}
                    themeColor={'primary'}
                    fillMode={'flat'}
                    className={pddBtnCls}
                    size={'small'}
                    aria-label={this.translate("SRL-openPhaseDueDatePopup")}
                    iconClass={`tp-icon tp-icon-sm ${this.isPhasesOverdue() ? 'tp-high-priority' : 'tp-calendar2'}`}
                    onClick={this.setExtra.bind(this, 'pdd')}
                    tooltipPosition={'right'}
                    withTooltip
                  />
                }
              </Field>
            </FieldWrap>
            <FieldWrap
              className={workflowsClass}
              withValidation
            >
              <Field>
                <FilterCombo
                  label={this.translate('workflow')}
                  data={workflows}
                  name={'workflowId'}
                  textField='name'
                  required={true}
                  disabled={submissionStarted}
                  handleFormChange={this.props.form.handleChange}
                  valueField="id"
                  onChange={onChange.bind(this, moduleId, undefined)}
                  popupSettings={{ className: zIndexCls }}
                  translateMethod={this.translate.bind(this)}
                  errorTxt={this.translateErrorMessage(workflowId)}
                  valueAsSimpleType={true}
                  withValidation
                  {...workflowId}
                />
              </Field>
            </FieldWrap>
            <FieldWrap className={targetLanguages}>
              <Field>
                {/*<span*/}
                {/*  className={'k-link tp-tms-toggler'}*/}
                {/*  onClick={this.setExtraDispatcher.bind(this, 'tms')}*/}
                {/*>*/}
                {/*  {this.state.extra && this.state.extraType === 'tms' ? this.translate('hideTM') : this.translate('showTM')}*/}
                {/*</span>*/}
                <PuzzleTagField
                  autoClose={false}
                  className={'tp-target-lang-tagfield'}
                  data={targetLanguageData}
                  dataItemKey='userId'
                  disabled={submissionStarted}
                  errorTxt={this.translateErrorMessage(targetLanguage)}
                  id={`${moduleId}-targetLanguage`}
                  itemColoring={targetLanguageItemColoring.bind(this)}
                  label={this.translate('targetLanguage')}
                  name={'targetLanguage'}
                  onChange={(event: MultiSelectChangeEvent) => this.onTargetLanguageChange(event, moduleId)}
                  onOpen={preventDisabledTagToOpen}
                  popupSettings={{ className: this.props.zIndexCls }}
                  required={true}
                  tagRender={(data: TM_DATA, li: ReactElement<any>) => tmTagRenderer(data, li, this.dispatch.bind(this), project, paClient)}
                  textField={'targetLanguageDisplayName'}
                  valueField={'targetLanguage'}
                  withValidation
                  {...targetLanguage}
                />
              </Field>
              {!isTemplate &&
                <PuzzleButton
                  titleText={this.translate("SRL-openTMPopup")}
                  ref={(el: any) => this.tmsButton = el}
                  themeColor={'primary'}
                  fillMode={'flat'}
                  className={extraType === 'tms' && extra ? 'k-selected' : ''}
                  size={'small'}
                  aria-label={this.translate("SRL-openTMPopup")}
                  iconClass={'tp-icon tp-tm tp-tm-toogle-icon'}
                  onClick={this.setExtraDispatcher.bind(this, 'tms')}
                  tooltipPosition={'right'}
                  withTooltip
                />
              }
            </FieldWrap>
          </Container>
          {!isTemplate &&
            <Container
              className={`tp-batch-section-right-side k-hbox k-justify-content-between`}
              htmlTag={'div'}
            >
              {this.state.extra && (this.state.extraType === 'tms' ?
                <TranslationMemories
                  batch={batch}
                  moduleId={moduleId}
                  onChange={onChange}
                  paClient={paClient}
                  popupLeaveFocusEl={this.tmsButton?.buttonRef.current}
                  projectId={project}
                  sourceLanguage={sourceLanguage}
                  submissionMode={submissionMode}
                  submissionStarted={submissionStarted}
                  targetFormat={targetFormat}
                  targetLanguages={targetLanguage.value}
                  targetLanguagesData={targetLanguageData}
                /> : null)}
              {this.props.values?.targetLanguageInfos && targetLanguagesForPhases?.length ?
                <PhaseDueDates
                  showComponent={this.state.extra && this.state.extraType === 'pdd'}
                  popupLeaveFocusEl={this.pddButton?.buttonRef.current}
                  allowEdit={!!hasEditPhaseDueDatePolicy}
                  phasesData={phasesData}
                  onChange={onChange}
                  moduleId={moduleId}
                  batchDueDate={this.props.values?.dueDate}
                  submissionDueDate={this.props.submissionDueDate}
                  targetLanguages={targetLanguagesForPhases}
                  targetLanguageInfos={this.props.values?.targetLanguageInfos}
                  multipleBatches={multipleBatches}
                  workflowId={workflowId}
                /> : null
              }
              <Container
                ariaLabel={this.translate('SRL-section-files')}
                className={'tp-batch-section-files-wrapper k-display-flex k-vbox-reverse  k-flex'}
                htmlTag={'section'}
                tabIndex={0}>
                <SourceFiles
                  batch={batch}
                  className={moduleId}
                  isCollapsed={this.state.extra}
                  isDisabled={this.disableUpload()}
                  hasFileConnection={true}
                  moduleId={moduleId}
                  moveToTargets={moveToTargets}
                  multiBatchDialog={true}
                  partiallyAnalyzed={partiallyAnalyzed}
                  projectFileFormats={projectFileFormats}
                  projectId={project}
                  selectionField={'selected'}
                  submissionStarted={submissionStarted}
                  submissionStatus={submissionStatus}
                  uploadFlowState={uploadFlowState}
                  zIndexCls={zIndexCls}
                />
                <h3 className={'tp-text-semibold tp-font-size-md tp-padding-top-2'}>{this.translate('filesLabel')}</h3>
              </Container>
            </Container>}
        </div>
        {isClosable && (<div className={deleteBatchBtnClass}>
          <PuzzleButton
            aria-label={this.translate("SRL-closeBatch")}
            fillMode={'solid'}
            themeColor={'dark'}
            className={'k-button-xs'}
            iconClass="tp-icon tp-close"
            onClick={onChange.bind(this, moduleId, 'delete')}
          />
        </div>)}
      </div>
    )
  }
}


export default AbstractSection;
